import React, { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Box, Typography, Avatar } from "@mui/material";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

const ProfilePictureSelect = forwardRef(function ProfilePictureSelect({
  label = "Image",
  header = "Change Image",
  url = "",
  onFileChange,
  ...props
}, ref) {
  const theme = useTheme();
  const inputRef = useRef();
  const [preview, setPreview] = useState(url);

  useImperativeHandle(ref, () => ({
    trigger: () => inputRef.current?.click(),
    getFile: () => inputRef.current?.files[0] || null,
  }));

  React.useEffect(() => {
    setPreview(url);
  }, [url]);

  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
      if (onFileChange) onFileChange(file, reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box sx={{ maxWidth: { xs: 180, sm: 220 }, width: "150px", mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: theme.palette.text.secondary,
          mb: 0.5,
          fontSize: { xs: 14, sm: 16 },
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "1",
          borderRadius: "12%",
          overflow: "hidden",
          cursor: "pointer",
          border: `1.5px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.default,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={() => inputRef.current?.click()}
      >
        <Avatar
          src={preview || url || "/image_placeholder.svg"}
          alt={label}
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: "0",
            objectFit: "cover",
            bgcolor: theme.palette.background.paper,
          }}
          variant="square"
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            bgcolor: "rgba(0,0,0,0.4)",
            color: "#fff",
            textAlign: "center",
            p: { xs: 0.5, sm: 1 },
            fontSize: { xs: 11, sm: 13 },
          }}
        >
          {header}
        </Box>
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleSelect}
        {...props}
      />
    </Box>
  );
});

ProfilePictureSelect.propTypes = {
  label: PropTypes.string,
  header: PropTypes.string,
  url: PropTypes.string,
  onFileChange: PropTypes.func,
  initialFile: PropTypes.any,
};

export default ProfilePictureSelect;
